import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { ChangePasswordNavigationService } from './change-password-navigation.service';
import { AccessRoute } from '../../shared/types/webview.types';
import { AppRoutes, ROUTE_TITLES } from '../../shared/constants/app-routes';
import { TranslateService } from './translate.service';
import { WebViewService } from './webview.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  public currentPath = signal('');
  public canGoBack = computed(() => {
    const currentPath = this.currentPath();
    return currentPath !== AppRoutes.HOME && currentPath !== '/';
  });

  constructor(
    private router: Router,
    private authService: AuthService,
    private changePasswordNavigationService: ChangePasswordNavigationService,
    private translateService: TranslateService,
    private webViewService: WebViewService
  ) {
    this.router.events.subscribe(() => {
      this.currentPath.set(this.router.url);
    });
  }

  // ===== NAVEGACIÓN BÁSICA =====
  navigateTo(path: string): void {
    this.router.navigate([path]);
  }

  goToHome(): void {
    this.router.navigate([AppRoutes.HOME]);
  }

  goToCreate(): void {
    this.router.navigate([AppRoutes.CREATE_PQRS]);
  }

  goToDetails(id: string): void {
    this.router.navigate([AppRoutes.PQRS_DETAILS, id]);
  }

  goToChangePassword(): void {
    this.router.navigate([AppRoutes.CHANGE_PASSWORD]);
  }

  goBack(): void {
    // Si estamos en change-password, usar la lógica específica
    if (this.currentPath() === AppRoutes.CHANGE_PASSWORD) {
      const handled = this.changePasswordNavigationService.handleBackNavigation();
      if (handled) {
        return;
      }
    }
    
    if(this.currentPath() === AppRoutes.CREATE_PUBLIC_PQRS) {
      this.webViewService.sendBackNative();
      return;
    }
    
    // Navegación por defecto
    this.goToHome();
  }

  // ===== NAVEGACIÓN CON VALIDACIÓN =====
  navigateToRoute(route: AccessRoute): boolean {
    if (this.authService.isAuthenticated()) {
      switch (route) {
        case AccessRoute.HOME:
          this.goToHome();
          return true;
        case AccessRoute.CREATE_PQRS:
          this.goToCreate();
          return true;
        default:
          return false;
      }
    }
    return false;
  }

  // ===== UTILIDADES =====
  getPageTitle(): string {
    const currentPath = this.currentPath();
    
    // Si las traducciones aún se están cargando, retornar un título por defecto
    if (this.translateService.isLoadingTranslations()) {
      return 'Cargando...';
    }
    
    // Para rutas con parámetros como /pqrs-details/123
    if (currentPath.startsWith(AppRoutes.PQRS_DETAILS + '/')) {
      return this.translateService.translate(ROUTE_TITLES[AppRoutes.PQRS_DETAILS]);
    }

    // Buscar la ruta exacta en el mapping
    const routeTitle = ROUTE_TITLES[currentPath as AppRoutes];
    if (routeTitle) {
      return this.translateService.translate(routeTitle);
    }

    // Título por defecto
    return this.translateService.translate(ROUTE_TITLES[AppRoutes.HOME]);
  }
}
